import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { DEVICE_SIZES, useDeviceSize } from 'rn-responsive-styles';

import { getName } from '@pletly/shared';
import { Table, Text, ActionsMenu, spacing, isDeviceMinimumSize } from '@pletly/ui';
import { useUser } from '%hooks';
import { FileLink } from '../FileLink';

export const FilesTable = <
	File extends {
		uuid: string;
		url: string;
		name?: string | null;
		owner?: Parameters<typeof getName>['0'] & { id: string };
		createdAt: number;
	},
>({
	files,
	onDeletePress,
}: {
	files: File[];
	onDeletePress?: (file: File) => void;
}) => {
	const { t } = useTranslation();
	const deviceSize = useDeviceSize();
	const isMinWidth = isDeviceMinimumSize(deviceSize, DEVICE_SIZES.MEDIUM_DEVICE);
	const user = useUser();

	const renderRowActions = useCallback(
		({ row }: { row: (typeof files)[number] }) => (
			<ActionsMenu
				actions={[
					{
						title: (
							<FileLink
								url={row.url}
								filename={row.name || ''}
								text={t('profile.files-table.download')}
								variant="small"
								download
							/>
						),
						onPress: () => null,
					},
					{
						title: t('common.delete'),
						onPress: () => onDeletePress?.(row),
						hidden: !onDeletePress || row.owner?.id !== user.id,
					},
				]}
			/>
		),
		[onDeletePress, t, user.id],
	);

	return (
		<View style={styles.root}>
			<Text category="h4">{t('profile.files')}</Text>

			<Table
				data={files}
				renderRowActions={renderRowActions}
				rowActionsWidth={40}
				columnsConfig={[
					{
						name: t('profile.files-table.name'),
						accessor: 'name',
						renderCell: ({ row: { url, name } }) => (
							<FileLink
								url={url}
								filename={name || ''}
								variant="medium"
								style={styles.fileLinkCell}
							/>
						),
					},
					isMinWidth
						? {
								name: t('profile.files-table.creator'),
								accessor: 'owner',
								renderCell: ({ row }) => <Text category="s2">{getName(row.owner)}</Text>,
							}
						: null,
					{
						name: t('profile.files-table.created-at'),
						accessor: 'createdAt',
						renderCell: ({ row }) => (
							<Text category="s2">{new Date(row.createdAt).toLocaleString()}</Text>
						),
					},
				].filter(Boolean)}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	root: {
		width: '100%',
		gap: spacing(2),
	},
	fileLinkCell: {
		marginEnd: spacing(),
	},
});
